import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';

interface BannerAdProps {
  position?: 'top' | 'sidebar' | 'inline' | 'bottom';
  style?: React.CSSProperties;
}

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT_ID || '';
const SLOT_MAP: Record<string, string> = {
  top:     process.env.REACT_APP_ADSENSE_SLOT_TOP     || '',
  inline:  process.env.REACT_APP_ADSENSE_SLOT_INLINE  || '',
  sidebar: process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR || '',
  bottom:  process.env.REACT_APP_ADSENSE_SLOT_BOTTOM  || '',
};

/* ── Google AdSense 광고 블록 ── */
const AdSenseBlock: React.FC<{ slot: string; position: string }> = ({ slot, position }) => {
  const ref = useRef<HTMLModElement>(null);

  useEffect(() => {
    try {
      // AdSense 초기화 트리거
      if (ref.current && (window as any).adsbygoogle) {
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
      }
    } catch (e) {
      console.warn('[BannerAd] AdSense push 실패:', e);
    }
  }, []);

  const heightMap: Record<string, number> = { top: 90, inline: 90, sidebar: 250, bottom: 90 };

  return (
    <div style={{ minHeight: heightMap[position] || 90, overflow: 'hidden' }}>
      <ins
        ref={ref}
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
};

/* ── 자체 광고 (AdSense 승인 전) ── */
const FallbackAd: React.FC<{ position: string; style?: React.CSSProperties }> = ({ position, style }) => {
  const { t } = useTranslation();
  const adMap: Record<string, { msg: string; cta: string; link: string; h: number }> = {
    top:     { msg: t('ad.topMsg'), cta: t('ad.topCta'), link: '/services', h: 52 },
    sidebar: { msg: t('ad.sidebarMsg'), cta: t('ad.sidebarCta'), link: '/services', h: 200 },
    inline:  { msg: t('ad.inlineMsg'), cta: t('home.findExperts'), link: '/services', h: 68 },
    bottom:  { msg: t('ad.bottomMsg'), cta: t('ad.bottomCta'), link: '/services', h: 48 },
  };
  const ad = adMap[position] || adMap.inline;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
      minHeight: ad.h,
      borderRadius: position === 'top' || position === 'bottom' ? 0 : 12,
      padding: '12px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
      color: '#fff', position: 'relative', overflow: 'hidden',
      ...style,
    }}>
      <span style={{
        position: 'absolute', top: 4, left: 8,
        fontSize: 10, opacity: 0.6,
        background: 'rgba(0,0,0,0.18)', padding: '1px 5px', borderRadius: 4,
      }}>{t('ad.label')}</span>
      <Zap size={15} style={{ opacity: 0.9, flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 600 }}>{ad.msg}</span>
      <Link to={ad.link} style={{
        padding: '6px 14px',
        background: 'rgba(255,255,255,0.18)',
        color: '#fff', textDecoration: 'none',
        borderRadius: 8, fontSize: 12, fontWeight: 600,
        border: '1px solid rgba(255,255,255,0.28)',
        whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        {ad.cta}
      </Link>
    </div>
  );
};

/* ── BannerAd 진입점 ── */
const BannerAd: React.FC<BannerAdProps> = ({ position = 'inline', style }) => {
  const slot = SLOT_MAP[position];

  // AdSense 클라이언트 ID와 슬롯 ID가 모두 있으면 실제 AdSense 사용
  if (ADSENSE_CLIENT && slot) {
    return <AdSenseBlock slot={slot} position={position} />;
  }

  // 그 외 자체 광고 표시
  return <FallbackAd position={position} style={style} />;
};

export default BannerAd;
